import { useEffect, useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { Box, Button, Container, Grid, useTheme } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { postNotification } from "../../../../../components/utilities/Notifications";
import { postData } from "../../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../../global/environmentVariables";
import ModalLoadingMessage from "../../../../../components/modals/ModalLoadingMessage";

const stripePromise = loadStripe(getEnvironmentVariable("STRIPE_PK"));

export function StripeCheckout(props) {
  const {
    event,
    transactionQueued,
    playerData,
    paymentData,
    setLoading,
    setTicketModal,
    clientSecret,
    setClientSecret,
    transactionId,
    setTransactionId,
    transactionOrderId,
    setTransactionOrderId
  } = props;
  const theme = useTheme();

  useEffect(() => {
    const go = async () => {
      try {
        if (!transactionQueued?.id) {
          throw new Error("Invalid transaction info, please try again");
        }

        // Only runs initially once
        if (transactionQueued?.status === "Pending Checkout") {
          var stripeIntent = await postData(
            `/transactions/${transactionQueued.id}/paymentIntent`,
            {
              body: {
                [event?.format?.teamEvent || event?.format?.doublesEvent
                  ? "captainData"
                  : "playerData"]: playerData,
                paymentData: paymentData
              }
            }
          );

          setTransactionId(stripeIntent?.transactionId);
          setTransactionOrderId(stripeIntent?.transactionOrderId);
          setClientSecret(stripeIntent?.clientSecret);
        }
      } catch (err) {
        postNotification("error", err);
        setTicketModal(
            event?.ticketing?.shippingDetails?.requested
            ? 3
            : 2
        );
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, []);

  const appearance = {
    theme: theme?.palette?.mode === "dark" ? "night" : "stripe"
  };
  const options = {
    clientSecret,
    appearance
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Container
        sx={{
          pt: 2,
          pb: 2
        }}
        maxWidth="lg"
      >
        {clientSecret ? (
          <Elements options={options} stripe={stripePromise}>
            <CheckoutForm
              transactionQueued={transactionQueued}
              transactionId={transactionId}
              transactionOrderId={transactionOrderId}
              setTicketModal={setTicketModal}
              event={event}
            />
          </Elements>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              minWidth: "100%",
              minHeight: "250px"
            }}
          ></Box>
        )}
      </Container>
    </Box>
  );
}

function CheckoutForm(props) {
  const { transactionId, transactionOrderId, setTicketModal, event } = props;
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${window.location.protocol}//${window.location.host}/transaction/${transactionId}/receipt?orderId=${transactionOrderId}`
      }
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occured.");
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <ExpressCheckoutElement
            onConfirm={async (event) => {
              const { error: submitError } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                  return_url: `${window.location.protocol}//${window.location.host}/transaction/${transactionId}/receipt?orderId=${transactionOrderId}`
                }
              });

              if (submitError) {
                setMessage(submitError.message);
              }
            }}
          />
        </Grid>

        {/* Separator between express and traditional payment - remove for now
        <Grid item xs={12} sx={{ textAlign: 'center', my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            or pay with card
          </Typography>
        </Grid> */}
        <Grid item xs={12} sx={{ minHeight: "230px" }}>
          <PaymentElement id="payment-element" />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Button
                disabled={isLoading}
                color="error"
                fullWidth
                variant="outlined"
                onClick={(e) => {
                  setTicketModal(
                      event?.ticketing?.shippingDetails?.requested
                      ? 3
                      : 2
                  );
                }}
              >
                Back
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                disabled={isLoading || !stripe || !elements}
                fullWidth
                id="submit"
                variant="contained"
                color="success"
                type="submit"
              >
                Pay now
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isLoading && <ModalLoadingMessage open={isLoading} message={message} />}
    </form>
  );
}

export function SquareCheckout(props) {
  const {
    event,
    setLoading,
    transactionQueued,
    playerData,
    paymentData,
    setTicketModal,
    transactionId,
    setTransactionId,
    setTransactionOrderId,
    setClientSecret,
    transactionOrderId
  } = props;

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const squareAppId = getEnvironmentVariable("SQUARE_APP_ID");
  const theme = useTheme();

  useEffect(() => {
    const go = async () => {
      try {
        if (!transactionQueued?.id) {
          throw new Error("Invalid transaction info, please try again");
        }

        // Only runs initially once
        if (transactionQueued?.status === "Pending Checkout") {
          const squareIntent = await postData(
            `/transactions/${transactionQueued.id}/paymentSession`,
            {
              [event?.format?.teamEvent || event?.format?.doublesEvent
                ? "captainData"
                : "playerData"]: playerData,
              paymentData: paymentData
            }
          );

          setTransactionId(squareIntent?.transactionId);
          setTransactionOrderId(squareIntent?.transactionOrderId);
          setClientSecret(squareIntent?.clientKey);
        }
      } catch (err) {
        postNotification("error", err);
        setTicketModal(
            event?.ticketing?.shippingDetails?.requested
            ? 3
            : 2
        );
      } finally {
        setLoading(false);
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <PaymentForm
      {...props}
      applicationId={squareAppId}
      locationId={event?.ticketing?.square?.locationId}
      cardTokenizeResponseReceived={async (token, verifiedBuyer) => {
        setIsLoading(true);

        try {
          setMessage("Processing payment...");
          const squareCharge = await postData(
            `/transactions/${transactionId}/charge`,
            { cardToken: token.token }
          );

          if (squareCharge) {
            setMessage("Payment succeeded!");

            // Redirect to receipt page
            window.location.assign(
              `/transaction/${transactionId}/receipt?orderId=${transactionOrderId}`
            );
          } else {
            throw new Error("Transaction failed, please try again.");
          }
        } catch (err) {
          setMessage(
            err.message || "An error occurred during payment processing."
          );
          postNotification("error", err);
        } finally {
          setIsLoading(false);
        }
      }}
    >
      <CreditCard
        buttonProps={{
          css: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            transition: "background-color 0.3s ease-in-out",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark
            }
          }
        }}
      />
      {isLoading && <ModalLoadingMessage open={isLoading} message={message} />}
    </PaymentForm>
  );
}
